import { faQuestionCircle, faTrash } from '@fortawesome/pro-regular-svg-icons';
import DOMPurify from 'dompurify';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { DndSortableList } from 'src/components/DndSortableList';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { TextField } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { isAdmin } from 'src/models/user/utils';

import { AssessmentFormValues } from '../AssessmentNewPage';


interface FormulaOperator {
  id: string;
  name: string;
  type: 'question' | 'operand';
}

interface Formula {
  lhs: FormulaOperator[];
  rhs: string;
  equality: '=' | '>' | '<';
  advanced_input?: boolean;
  advanced_formula?: string;
  next_question_link?: 'AND' | 'OR';
}

interface AssessmentRuleFormulaProps {
  formula: Formula;
  index: number;
  ruleIndex: number;
  formulas: Formula[];
  onRemove: (formula: Formula) => void;
}

export const AssessmentRuleFormula = ({
  formula,
  index,
  ruleIndex,
  formulas,
  onRemove,
}: AssessmentRuleFormulaProps) => {
  const { t: tCommon } = useTranslation('translation');
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessment.rules',
  });
  const { values, setFieldValue } = useFormikContext<AssessmentFormValues>();
  const { user } = useSession();

  const [selectedQuestion, setSelectedQuestion] = useState<string>('');
  const [selectedOperand, setSelectedOperand] = useState<string>('');

  const isUserAdmin = user ? isAdmin(user) : false;
  const hasNextFormula = formulas[index + 1] !== undefined;

  const operands = [
    { id: '+', name: '+' },
    { id: '-', name: '-' },
    { id: '*', name: '×' },
    { id: '/', name: '÷' },
    { id: 'or', name: 'OR' },
    { id: 'and', name: 'AND' },
    { id: '(', name: '(' },
    { id: ')', name: ')' },
  ];

  const equalityOptions = [
    { value: '=', label: '=' },
    { value: '>', label: '>' },
    { value: '<', label: '<' },
  ];

  const linkWords = ['AND', 'OR'];

  // Generate questions from fields
  const questions = (values.fields || []).flatMap((field, fieldIndex) => {
    const questionNum = fieldIndex + 1;
    return [
      {
        id: `qv${questionNum}`,
        name: t('questionValue', { number: questionNum }),
      },
      {
        id: `q${questionNum}`,
        name: t('questionPoints', { number: questionNum }),
      },
    ];
  });

  const handleToggleAdvancedInput = () => {
    const currentRules = values.rules || [];
    if (ruleIndex >= currentRules.length) return;

    const updatedRules = [...currentRules];
    const rule = updatedRules[ruleIndex];
    const updatedFormulas = [...(rule.formulas || [])];
    updatedFormulas[index] = {
      ...updatedFormulas[index],
      advanced_input: !updatedFormulas[index].advanced_input,
    };
    updatedRules[ruleIndex] = {
      ...rule,
      formulas: updatedFormulas,
    };
    setFieldValue('rules', updatedRules);
  };

  const handleRemoveFormula = () => {
    onRemove(formula);
  };

  const handleAdvancedFormulaChange = (value: string) => {
    const currentRules = values.rules || [];
    if (ruleIndex >= currentRules.length) return;

    const updatedRules = [...currentRules];
    const rule = updatedRules[ruleIndex];
    const updatedFormulas = [...(rule.formulas || [])];
    updatedFormulas[index] = {
      ...updatedFormulas[index],
      advanced_formula: value,
    };
    updatedRules[ruleIndex] = {
      ...rule,
      formulas: updatedFormulas,
    };
    setFieldValue('rules', updatedRules);
  };

  const handleLhsChange = (newLhs: FormulaOperator[]) => {
    const currentRules = values.rules || [];
    if (ruleIndex >= currentRules.length) return;

    const updatedRules = [...currentRules];
    const rule = updatedRules[ruleIndex];
    const updatedFormulas = [...(rule.formulas || [])];
    updatedFormulas[index] = {
      ...updatedFormulas[index],
      lhs: newLhs,
    };
    updatedRules[ruleIndex] = {
      ...rule,
      formulas: updatedFormulas,
    };
    setFieldValue('rules', updatedRules);
  };

  const handleEqualityChange = (value: string) => {
    const currentRules = values.rules || [];
    if (ruleIndex >= currentRules.length) return;

    const updatedRules = [...currentRules];
    const rule = updatedRules[ruleIndex];
    const updatedFormulas = [...(rule.formulas || [])];
    updatedFormulas[index] = {
      ...updatedFormulas[index],
      equality: value as '=' | '>' | '<',
    };
    updatedRules[ruleIndex] = {
      ...rule,
      formulas: updatedFormulas,
    };
    setFieldValue('rules', updatedRules);
  };

  const handleRhsChange = (value: string) => {
    const currentRules = values.rules || [];
    if (ruleIndex >= currentRules.length) return;

    const updatedRules = [...currentRules];
    const rule = updatedRules[ruleIndex];
    const updatedFormulas = [...(rule.formulas || [])];
    updatedFormulas[index] = {
      ...updatedFormulas[index],
      rhs: value,
    };
    updatedRules[ruleIndex] = {
      ...rule,
      formulas: updatedFormulas,
    };
    setFieldValue('rules', updatedRules);
  };

  const handleAddQuestion = (
    _event: React.SyntheticEvent,
    value: { id: string; name: string } | null
  ) => {
    if (!value) {
      setSelectedQuestion('');
      return;
    }
    const question = questions.find((q) => q.id === value.id);
    if (!question) {
      setSelectedQuestion('');
      return;
    }

    const newOperator: FormulaOperator = {
      type: 'question',
      id: question.id,
      name: question.name,
    };
    handleLhsChange([...formula.lhs, newOperator]);
    setSelectedQuestion('');
  };

  const handleAddOperand = (
    _event: React.SyntheticEvent,
    value: { id: string; name: string } | null
  ) => {
    if (!value) {
      setSelectedOperand('');
      return;
    }
    const operand = operands.find((o) => o.id === value.id);
    if (!operand) {
      setSelectedOperand('');
      return;
    }

    const newOperator: FormulaOperator = {
      type: 'operand',
      id: operand.id,
      name: operand.name,
    };
    handleLhsChange([...formula.lhs, newOperator]);
    setSelectedOperand('');
  };

  const handleRemoveOperator = (operator: FormulaOperator) => {
    handleLhsChange(
      formula.lhs.filter((o, i) => {
        const opId = `${o.type}-${o.id}-${i}`;
        return opId !== operator.id;
      })
    );
  };

  const handleNextQuestionLinkChange = (value: string) => {
    const currentRules = values.rules || [];
    if (ruleIndex >= currentRules.length) return;

    const updatedRules = [...currentRules];
    const rule = updatedRules[ruleIndex];
    const updatedFormulas = [...(rule.formulas || [])];
    updatedFormulas[index] = {
      ...updatedFormulas[index],
      next_question_link: value as 'AND' | 'OR',
    };
    updatedRules[ruleIndex] = {
      ...rule,
      formulas: updatedFormulas,
    };
    setFieldValue('rules', updatedRules);
  };

  // Ensure lhs items have unique IDs for drag and drop
  const lhsItems = formula.lhs.map((op, i) => ({
    ...op,
    id: `${op.type}-${op.id}-${i}`,
  }));

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              {t('formula')} {index + 1}
            </Typography>
            <Button size="small" onClick={handleRemoveFormula}>
              <Icon
                icon={faTrash}
                size="small"
              />
            </Button>
          </Box>
        </Grid>
      </Grid>
      {formula.advanced_input ? (
        <Box
          sx={{
            borderBottom: '1px solid #dedede',
            marginBottom: 2,
            paddingBottom: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  placeholder={t('enterFormula')}
                  value={formula.advanced_formula || ''}
                  onChange={(e) => handleAdvancedFormulaChange(e.target.value)}
                  disabled={!isUserAdmin}
                  fullWidth
                />
                {isUserAdmin ? (
                  <Button onClick={handleToggleAdvancedInput} size="small">
                    {t('useFormulaInput')}
                  </Button>
                ) : (
                  <Tooltip
                    title={t('adminEditTooltip')}
                    placement="top"
                  >
                    <Box component="span" sx={{ ml: 1 }}>
                      <Icon
                        icon={faQuestionCircle}
                        size="small"
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  float: 'left',
                }}
              >
                <Box
                  component="ul"
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                  }}
                >
                  <DndSortableList
                    items={lhsItems}
                    setItems={(items) => {
                      // Extract the original id from the composite id
                      const itemsArray =
                        typeof items === 'function' ? items(lhsItems) : items;
                      handleLhsChange(
                        itemsArray.map((item) => {
                          // item.id format: "type-originalId-index"
                          const parts = item.id.split('-');
                          const originalId =
                            parts.slice(1, -1).join('-') || item.id;
                          return {
                            type: item.type,
                            id: originalId,
                            name: item.name,
                          };
                        })
                      );
                    }}
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      flexWrap: 'wrap',
                      listStyle: 'none',
                      padding: 0,
                    }}
                  >
                    {(item) => (
                      <Box
                        component="li"
                        sx={{
                          display: 'inline-block',
                          cursor: 'grab',
                          padding: '4px 8px',
                          backgroundColor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Button
                          size="small"
                          onClick={() => handleRemoveOperator(item)}
                          sx={{
                            minWidth: 'auto',
                            padding: 0,
                            textTransform: 'none',
                            color: 'inherit',
                          }}
                        >
                          <Box
                            component="span"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(item.name),
                            }}
                          />
                        </Button>
                      </Box>
                    )}
                  </DndSortableList>
                  <Box component="li" sx={{ display: 'inline-block' }}>
                    <TextField
                      value={formula.equality || '='}
                      onChange={(e) => handleEqualityChange(e.target.value)}
                      options={equalityOptions}
                      sx={{ width: '80px' }}
                    />
                  </Box>
                </Box>
                <Box sx={{ float: 'left', paddingLeft: '5px' }}>
                  <TextField
                    value={formula.rhs || ''}
                    onChange={(e) => handleRhsChange(e.target.value)}
                    sx={{ width: '150px' }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              borderBottom: '1px solid #dedede',
              marginBottom: 2,
              paddingBottom: 2,
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography>{t('addQuestion')}</Typography>
                  <Tooltip
                    title={t('questionValueTooltip', {
                      assessment: tCommon('assessment.one'),
                    })}
                    placement="bottom"
                  >
                    <Icon
                      icon={faQuestionCircle}
                      size="small"
                    />
                  </Tooltip>
                </Box>
                <Autocomplete
                  options={questions}
                  value={
                    selectedQuestion
                      ? questions.find((q) => q.id === selectedQuestion) || null
                      : null
                  }
                  onChange={handleAddQuestion}
                  getOptionLabel={(option) => option.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('addQuestionPlaceholder')}
                      fullWidth
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography sx={{ mb: 1 }}>{t('addOperand')}</Typography>
                <Autocomplete
                  options={operands}
                  value={
                    selectedOperand
                      ? operands.find((o) => o.id === selectedOperand) || null
                      : null
                  }
                  onChange={handleAddOperand}
                  getOptionLabel={(option) => option.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('addOperandPlaceholder')}
                      fullWidth
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                />
              </Grid>
              {isUserAdmin && !formula.advanced_input && (
                <Grid size={{ xs: 12 }}>
                  <Button onClick={handleToggleAdvancedInput} size="small">
                    {t('useAdvancedInput')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      )}
      {hasNextFormula && (
        <Box
          sx={{
            borderBottom: '1px solid #dedede',
            marginBottom: 2,
            paddingBottom: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ textAlign: 'center', margin: '0 auto' }}>
                <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                  {t('linkFormula', { index1: index + 1, index2: index + 2 })}
                </Typography>
                <TextField
                  value={formula.next_question_link || 'AND'}
                  onChange={(e) => handleNextQuestionLinkChange(e.target.value)}
                  options={linkWords.map((word) => ({
                    value: word,
                    label: word,
                  }))}
                  fullWidth
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};
